// SPEC-28 · the recap after the last question, and the same screen read-only
// once the assessment is confirmed. Before: an orange warning, every answer
// with « Alterar », one « Confirmar respostas ». After: a violet note, the
// lines as text, « Voltar ». The lines come from recap.ts.

import { copy } from "../i18n/pt-PT";
import type { RecapLine } from "./recap";

const text = copy.questionnaire;
const words = text.recap;

const wideButton =
  "flex h-[var(--control-h)] w-full items-center justify-center gap-2 rounded-pill px-6 text-body font-semibold " +
  "transition-colors duration-[var(--dur-micro)] ease-pyf";
const iconButton =
  "flex size-[var(--touch-min)] shrink-0 items-center justify-center rounded-pill bg-violet-tint text-title text-dark " +
  "transition-colors duration-[var(--dur-micro)] ease-pyf hover:bg-cream-deep";
const tag = "rounded-pill px-3 py-1 text-caption font-semibold";

export function Recap({
  kicker,
  lines,
  count,
  readOnly,
  busy,
  failed,
  onBack,
  onChange,
  onConfirm,
}: {
  kicker: string;
  lines: readonly RecapLine[];
  /** « 4 perguntas · toca numa resposta para a alterar ». */
  count: string;
  readOnly: boolean;
  busy: boolean;
  failed: boolean;
  onBack: () => void;
  onChange: (questionId: string) => void;
  onConfirm: () => void;
}) {
  return (
    <main className="screen-enter flex min-h-dvh flex-col gap-5 bg-cream px-5 pt-4 pb-5">
      <header className="flex items-center justify-between">
        <button type="button" onClick={onBack} className={iconButton}>
          <span aria-hidden="true">←</span>
          {/* Before confirming, ← is the last question; read-only, it is the way back. */}
          <span className="sr-only">{readOnly ? text.back : text.previousScreen}</span>
        </button>
        {readOnly ? (
          <span className={`${tag} bg-lime-tint text-lime-ink`}>{words.done}</span>
        ) : (
          <span className={`${tag} bg-violet-tint text-violet-ink`}>{words.pending}</span>
        )}
      </header>

      <div className="flex flex-col gap-2">
        <p className="text-label font-semibold tracking-[var(--ls-label)] text-violet uppercase">{kicker}</p>
        <h1 className="text-display leading-[var(--lh-display)] font-semibold tracking-[var(--ls-display)]">
          {readOnly ? words.titleDone : words.title}
        </h1>
        <p className="text-caption text-muted">{readOnly ? words.countDone : count}</p>
      </div>

      <section
        aria-label={readOnly ? words.doneTitle : words.warningTitle}
        className={`flex flex-col gap-1 rounded-card-inner p-4 ${readOnly ? "bg-violet-tint text-violet-ink" : "bg-orange-tint text-orange-ink"}`}
      >
        <p className="text-body-sm font-semibold">{readOnly ? words.doneTitle : words.warningTitle}</p>
        <p className="text-body-sm leading-[var(--lh-body)]">{readOnly ? words.doneText : words.warning}</p>
      </section>

      <ul data-testid="recap" className="flex flex-col rounded-card bg-white px-4">
        {lines.map((line) => {
          const body = (
            <span className="flex min-w-0 flex-1 flex-col gap-1">
              <span className="text-caption-sm text-muted">{line.question}</span>
              <span className="text-body-sm font-semibold text-dark">{line.answer}</span>
            </span>
          );
          return (
            <li key={line.id} className="border-b border-line-8 last:border-b-0">
              {readOnly ? (
                <div className="flex min-h-[var(--touch-min)] items-center py-3">{body}</div>
              ) : (
                <button
                  type="button"
                  onClick={() => onChange(line.id)}
                  className="flex min-h-[var(--touch-min)] w-full items-center gap-4 py-3 text-left"
                >
                  {body}
                  <span className="shrink-0 text-body-sm font-semibold text-violet">{words.change}</span>
                </button>
              )}
            </li>
          );
        })}
      </ul>

      <div className="flex-1" />
      {failed && (
        <p role="alert" className="text-body-sm text-orange-ink">
          {words.failed}
        </p>
      )}
      {readOnly ? (
        <button type="button" onClick={onBack} className={`${wideButton} border border-line-20 bg-white text-dark hover:bg-cream-deep`}>
          {words.back}
        </button>
      ) : (
        <button
          type="button"
          disabled={busy}
          onClick={onConfirm}
          className={`${wideButton} bg-dark text-cream hover:bg-dark-hover disabled:bg-line-10 disabled:text-disabled`}
        >
          {busy ? words.saving : words.confirm}
        </button>
      )}
    </main>
  );
}
